import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from 'beam'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from '../../Helpers/axiosTypes'
import { isC1ApiError } from '../../Helpers/c1ApiTypes'
import { sendPasswordResetEmail } from './loginApi'

export interface ForgotPasswordDialogProps {
  /** Email address with which to pre-populate the email input; changing this will reset the email input */
  initialEmail: string,
  /** Called when the dialog should be closed due to cancellation (via open={false} in the parent component) */
  onCancel: () => void,
  /** Called when submission has failed for any reason */
  onError: (message: string, requestId?: string) => void,
  /** Called when submission has succeeded; parent component should close this dialog (via open={false}) */
  onSuccess: (email: string) => void,
  /** Whether this dialog is open; true if open; false if closed */
  open: boolean,
}

export function ForgotPasswordDialog({ initialEmail, onCancel, onError, onSuccess, open }: ForgotPasswordDialogProps): ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [email, setEmail] = useState(initialEmail)
  const isValid = email.length > 0
  const { t } = useTranslation()
  async function submit() {
    setIsSubmitting(true)
    try {
      await sendPasswordResetEmail(email)
      onSuccess(email)
    } catch (e) {
      if (!isAxiosError(e)) onError(t('error2'))
      else if (!e.response) onError(t('serverError'))
      else if (!isC1ApiError(e.response.data)) onError(e.response.statusText)
      else {
        onError(e.response.data.errorMessage, e.response.data.requestId)
      }
    } finally {
      setIsSubmitting(false)
    }
  }
  return <>
    <Dialog open={open} onClose={onCancel}>
      <form onSubmit={submit}>
        <DialogTitle>{t('forgotPassword')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('emailAddress')}
            value={email}
            onChange={event => setEmail(event.target.value)}
            disabled={isSubmitting}
            inputProps={{ 'data-testid': 'ForgotPasswordDialog-EmailInput' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={onCancel}
            data-testid={'ForgotPasswordDialog-CancelButton'}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            color="secondary"
            data-testid={'ForgotPasswordDialog-SubmitButton'}
          >
            {t('emailPasswordReset')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  </>
}
